import { useState, type ReactNode } from 'react';
import type React from 'react';
import { WithTooltip } from './Tooltip.js';

export type IconButtonVariant = 'solid' | 'outline' | 'ghost';
export type IconButtonTone    = 'cyan' | 'amber' | 'danger' | 'muted' | 'neutral' | 'yellow';
export type IconButtonSize    = 'sm' | 'md' | 'lg';

export type IconButtonShape = 'rounded' | 'circle';

export interface IconButtonProps {
  icon: ReactNode;
  ariaLabel: string;
  onClick?: () => void;
  /** Para barras que actúan sobre una selección viva (el editor de páginas):
   *  `onMouseDown={e => e.preventDefault()}` evita que el botón robe el foco y
   *  se pierda lo seleccionado dentro del iframe antes de que corra onClick. */
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: IconButtonVariant;
  tone?: IconButtonTone;
  size?: IconButtonSize;
  shape?: IconButtonShape;
  disabled?: boolean;
  /** Proceso en curso: el icono gira y el botón deja de aceptar clics. Un
   *  botón de icono sin texto no puede decir "cargando…", así que lo dice
   *  girando — mismo criterio que BandIconButton en la banda del editor. */
  loading?: boolean;
  tooltip?: string;
  /** Anclaje del tooltip flotante. Botón al inicio de una barra → 'left'. */
  tooltipAlign?: 'left' | 'right' | 'center';
  active?: boolean;
}

// ── Colour tables (mirrors Button.tsx) ───────────────────────────────────────

type Clr = { bg: string; color: string; border: string };

const SOLID: Record<IconButtonTone, Clr> = {
  cyan:    { bg: 'var(--evo-cyan)',    color: 'var(--evo-fg-on-cyan)', border: 'transparent' },
  amber:   { bg: 'var(--evo-amber)',   color: 'var(--evo-bg)',         border: 'transparent' },
  yellow:  { bg: '#eab308',           color: '#0a0a0a',               border: 'transparent' },
  danger:  { bg: 'var(--evo-danger)',  color: '#fff',                  border: 'transparent' },
  muted:   { bg: 'var(--evo-bg-3)',    color: 'var(--evo-fg-1)',       border: 'transparent' },
  neutral: { bg: 'var(--evo-fg-1)',    color: 'var(--evo-bg)',         border: 'transparent' },
};

const OUTLINE: Record<IconButtonTone, { base: Clr; hover: Clr }> = {
  cyan:    { base: { bg: 'transparent', color: 'var(--evo-cyan)',    border: 'var(--evo-cyan)'    },
             hover: { bg: 'var(--evo-cyan)',         color: 'var(--evo-fg-on-cyan)', border: 'var(--evo-cyan)'    } },
  amber:   { base: { bg: 'transparent', color: 'var(--evo-amber)',   border: 'var(--evo-amber)'   },
             hover: { bg: 'rgba(245,158,11,0.08)',   color: 'var(--evo-amber)',       border: 'var(--evo-amber)'   } },
  yellow:  { base: { bg: 'transparent', color: '#eab308',            border: '#eab308'            },
             hover: { bg: 'rgba(234,179,8,0.08)',    color: '#eab308',               border: '#eab308'            } },
  danger:  { base: { bg: 'transparent', color: 'var(--evo-danger)',  border: 'var(--evo-danger)'  },
             hover: { bg: 'rgba(239,68,68,0.08)',    color: 'var(--evo-danger)',      border: 'var(--evo-danger)'  } },
  muted:   { base: { bg: 'transparent', color: 'var(--evo-fg-2)',    border: 'var(--evo-stroke)'  },
             hover: { bg: 'var(--evo-panel)',         color: 'var(--evo-fg-2)',        border: 'var(--evo-steel)'   } },
  neutral: { base: { bg: 'transparent', color: 'var(--evo-fg-2)',    border: 'var(--evo-stroke)'  },
             hover: { bg: 'var(--evo-panel)',         color: 'var(--evo-fg-1)',        border: 'var(--evo-stroke)'  } },
};

const GHOST_CLR: Record<IconButtonTone, string> = {
  cyan: 'var(--evo-cyan)', amber: 'var(--evo-amber)', yellow: '#eab308',
  danger: 'var(--evo-danger)', muted: 'var(--evo-fg-3)', neutral: 'var(--evo-fg-2)',
};

const SZ: Record<IconButtonSize, string> = { sm: '28px', md: 'var(--evo-control-h)', lg: '40px' };

// ── IconButton ────────────────────────────────────────────────────────────────

export function IconButton({
  icon,
  ariaLabel,
  onClick,
  onMouseDown,
  variant = 'ghost',
  tone = 'muted',
  size = 'md',
  shape = 'rounded',
  disabled = false,
  loading = false,
  tooltip,
  tooltipAlign = 'right',
  active = false,
}: IconButtonProps) {
  const [hovered, setHovered] = useState(false);
  const h = hovered && !disabled;

  let clr: Clr;
  let opacity = disabled ? 0.5 : 1;

  if (variant === 'solid') {
    clr = SOLID[tone];
    if (h) opacity = 0.88;
  } else if (variant === 'outline') {
    clr = h ? OUTLINE[tone].hover : OUTLINE[tone].base;
  } else {
    if (active) {
      clr = { bg: 'rgba(0,250,200,0.12)', color: 'var(--evo-cyan)', border: 'transparent' };
    } else {
      clr = { bg: h ? 'var(--evo-bg-3)' : 'transparent', color: GHOST_CLR[tone], border: 'transparent' };
    }
  }

  const dim = SZ[size];
  const inerte = disabled || loading;
  const tipText = tooltip ?? ariaLabel;

  return (
    <WithTooltip label={tipText} align={tooltipAlign}>
    <button
      type="button"
      aria-label={ariaLabel}
      title={tipText}
      disabled={inerte}
      onClick={loading ? undefined : onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: dim,
        height: dim,
        padding: 0,
        flexShrink: 0,
        background: clr.bg,
        color: clr.color,
        border: clr.border === 'transparent' ? '1px solid transparent' : `1px solid ${clr.border}`,
        borderRadius: shape === 'circle' ? '50%' : 'var(--evo-r)',
        cursor: loading ? 'progress' : disabled ? 'not-allowed' : 'pointer',
        opacity,
        userSelect: 'none',
        transition: 'opacity 120ms linear, background 120ms linear, color 120ms linear, border-color 120ms linear',
        boxSizing: 'border-box',
        outline: 'none',
      }}
    >
      <span style={{ display: 'inline-flex', animation: loading ? 'evo-spin 0.8s linear infinite' : 'none' }}>
        {icon}
      </span>
    </button>
    </WithTooltip>
  );
}
